/**
 * Season Regulations Data
 *
 * Local data file for state deer season opener/closer dates by season type and year.
 * Keyed by state abbreviation → year → SeasonRegulation[].
 *
 * Update policy: refresh annually in spring when states publish regulations (typically March–July).
 * When new data is available, update `lastUpdated` and add the new year's array.
 *
 * Sources (the URL each state's data was last verified against — see the per-state
 * SOURCE comments below for the verification date):
 *   WI — https://dnr.wisconsin.gov/topic/hunt/dates
 *   MN — https://www.dnr.state.mn.us/hunting/seasons.html
 *   MI — https://content.govdelivery.com/accounts/MIDNR/bulletins/4250286 (official MI DNR bulletin)
 *   IA — https://www.iowadnr.gov/things-do/hunting-trapping/iowa-hunting-seasons
 *   IL — https://huntillinois.org/deer (official IDNR Hunt Illinois)
 *   OH — https://codes.ohio.gov/ohio-administrative-code/rule-1501:31-15-11 (OAC deer regulations)
 *   PA — https://www.pa.gov/agencies/pgc/newsroom/preliminary-2026-27-hunting-seasons-and-bag-limits-approved
 */

import { SeasonType } from '@/types/season';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SeasonRegulation {
  seasonType: SeasonType;
  openerDate: string; // YYYY-MM-DD
  closerDate: string; // YYYY-MM-DD
  name: string; // e.g., "9-Day Gun Deer"
  notes?: string; // zone restrictions, special conditions, etc.
}

export interface StateRegulationMeta {
  dnrUrl: string; // Link shown in stale-data warning
  lastUpdated: string; // YYYY-MM-DD — date this state's data was last verified against DNR source
}

interface StateRegulationData extends StateRegulationMeta {
  seasons: Record<number, SeasonRegulation[]>; // fall-year → regulations
}

// ─── Wisconsin ───────────────────────────────────────────────────────────────
// SOURCE: https://dnr.wisconsin.gov/topic/hunt/dates  (verified 2026-10-19)

const WI: StateRegulationData = {
  dnrUrl: 'https://dnr.wisconsin.gov/topic/hunt/deer',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-09-12',
        closerDate: '2027-01-03',
        name: 'Archery Deer',
        notes: 'Extended to Jan 31 in select metro subunits and counties.',
      },
      {
        seasonType: SeasonType.Crossbow,
        openerDate: '2026-09-12',
        closerDate: '2027-01-03',
        name: 'Crossbow Deer',
        notes: 'Runs concurrently with archery. Extended to Jan 31 in select areas.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-21',
        closerDate: '2026-11-29',
        name: '9-Day Gun Deer',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-11-30',
        closerDate: '2026-12-09',
        name: 'Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2026-10-10',
        closerDate: '2026-10-11',
        name: 'Youth/Disabled Gun Deer Hunt',
      },
    ],
  },
};

// ─── Minnesota ───────────────────────────────────────────────────────────────
// SOURCE: https://www.dnr.state.mn.us/hunting/seasons.html  (verified 2026-10-19)

const MN: StateRegulationData = {
  dnrUrl: 'https://www.dnr.state.mn.us/hunting/deer/index.html',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-09-19',
        closerDate: '2026-12-31',
        name: 'Archery Deer',
        notes: 'Statewide.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-07',
        closerDate: '2026-11-22',
        name: 'Firearms Season A',
        notes: '100-series permit areas statewide; 200 & 300 series close Nov 15.',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-11-28',
        closerDate: '2026-12-13',
        name: 'Muzzleloader Deer',
        notes: 'Statewide.',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2026-10-15',
        closerDate: '2026-10-18',
        name: 'Youth Deer Hunt',
      },
    ],
  },
};

// ─── Michigan ────────────────────────────────────────────────────────────────
// SOURCE: https://content.govdelivery.com/accounts/MIDNR/bulletins/4250286  (verified 2026-10-19)
//   — official MI DNR "2026 deer hunting regulations" bulletin. Corroborated by the NRC
//   bulletin https://content.govdelivery.com/accounts/MIDNR/bulletins/41773a3.
// NOTE: www.michigan.gov refuses automated requests (HTTP 403), so verification was done
// against the MI DNR's own GovDelivery bulletins rather than the deer page linked in
// `dnrUrl`. For 2026 the NRC cut the statewide muzzleloader season from 10 days to three,
// beginning the first Friday in December, and renamed the Lower Peninsula instance of that
// window the "December Firearm Deer Season".

const MI: StateRegulationData = {
  dnrUrl: 'https://www.michigan.gov/dnr/things-to-do/hunting/deer',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-10-01',
        closerDate: '2026-11-14',
        name: 'Early Archery Deer',
        notes: 'Statewide. Season resumes Dec 1 – Jan 1 after firearm season.',
      },
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-12-01',
        closerDate: '2027-01-01',
        name: 'Late Archery Deer',
        notes: 'Resumes after firearm and muzzleloader seasons.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-15',
        closerDate: '2026-11-30',
        name: 'Regular Firearm Deer',
        notes: 'Statewide. Dates are set by the Natural Resources Commission via the Wildlife Conservation Order.',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-12-04',
        closerDate: '2026-12-06',
        name: 'Muzzleloader Deer',
        notes:
          'Three-day season opening the first Friday of December. Muzzleloader in the Upper Peninsula; in the Lower Peninsula the same window is the December Firearm Deer Season, where any legal firearm may be used.',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2026-09-12',
        closerDate: '2026-09-13',
        name: 'Youth Liberty Hunt',
        notes: 'Open to youth hunters and hunters with qualifying disabilities.',
      },
    ],
  },
};

// ─── Iowa ────────────────────────────────────────────────────────────────────
// SOURCE: https://www.iowadnr.gov/things-do/hunting-trapping/iowa-hunting-seasons  (verified 2026-10-19)
// NOTE: The 2026 array below is the published Iowa 2026/27 season. The 2025 array is retained
// as the prior season's record.

const IA: StateRegulationData = {
  dnrUrl: 'https://www.iowadnr.gov/things-do/hunting-trapping/types-hunting-trapping/deer-hunting',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-10-01',
        closerDate: '2026-12-04',
        name: 'Archery Deer (Early)',
        notes: 'Archery pauses for gun seasons. Season resumes Dec 21 – Jan 10, 2027.',
      },
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-12-21',
        closerDate: '2027-01-10',
        name: 'Archery Deer (Late)',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-10-17',
        closerDate: '2026-10-25',
        name: 'Early Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-12-21',
        closerDate: '2027-01-10',
        name: 'Late Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-12-05',
        closerDate: '2026-12-09',
        name: 'First Shotgun Season',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-12-12',
        closerDate: '2026-12-20',
        name: 'Second Shotgun Season',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2026-09-19',
        closerDate: '2026-10-04',
        name: 'Youth Deer Season',
      },
    ],
    2025: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2025-10-01',
        closerDate: '2025-12-05',
        name: 'Archery Deer (Early)',
        notes: 'Archery pauses for gun seasons. Season resumes Dec 22 – Jan 10.',
      },
      {
        seasonType: SeasonType.Bow,
        openerDate: '2025-12-22',
        closerDate: '2026-01-10',
        name: 'Archery Deer (Late)',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2025-10-11',
        closerDate: '2025-10-19',
        name: 'Early Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2025-12-22',
        closerDate: '2026-01-10',
        name: 'Late Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2025-12-06',
        closerDate: '2025-12-10',
        name: 'First Shotgun Season',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2025-12-13',
        closerDate: '2025-12-21',
        name: 'Second Shotgun Season',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2025-09-20',
        closerDate: '2025-10-05',
        name: 'Youth Deer Season',
      },
    ],
  },
};

// ─── Illinois ────────────────────────────────────────────────────────────────
// SOURCE: https://huntillinois.org/deer  (verified 2026-10-19) — official IDNR Hunt Illinois
// season table. dnr.illinois.gov/hunting/deerhunting.html links out to it rather than
// listing the dates inline.

const IL: StateRegulationData = {
  dnrUrl: 'https://dnr.illinois.gov/hunting/deerhunting.html',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-10-01',
        closerDate: '2026-11-19',
        name: 'Archery Deer (Early)',
        notes: 'First archery window. Season resumes Nov 23 between firearm seasons.',
      },
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-11-23',
        closerDate: '2026-12-02',
        name: 'Archery Deer (Mid)',
        notes: 'Between first and second firearm seasons.',
      },
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-12-07',
        closerDate: '2027-01-17',
        name: 'Archery Deer (Late)',
        notes: 'In Cook, DuPage, Lake and Kane County east of Route 47, archery is open Oct 1 – Jan 17 continuous.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-20',
        closerDate: '2026-11-22',
        name: 'First Firearm Season',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-12-03',
        closerDate: '2026-12-06',
        name: 'Second Firearm Season',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-12-11',
        closerDate: '2026-12-13',
        name: 'Muzzleloader Deer',
      },
    ],
  },
};

// ─── Ohio ─────────────────────────────────────────────────────────────────────
// SOURCE: https://codes.ohio.gov/ohio-administrative-code/rule-1501:31-15-11  (verified 2026-10-19)
//   — Ohio Administrative Code 1501:31-15-11 "Deer regulations", effective 2026-07-06.
// NOTE: The rule states most Ohio deer seasons as weekday formulas rather than literal dates.
// The 2026 array below resolves them for the 2026/27 season: archery = last Saturday of
// September (2026-09-26) through the first Sunday of February (2027-02-07); gun = the first
// Monday after Thanksgiving (2026-11-30) through the following Sunday, plus Dec 19–20;
// muzzleloader = the first Saturday after New Year's Day (2027-01-02) through the following
// Tuesday. Youth gun is given literally in the rule as Nov 21–22, 2026.
// The rule's separate Disease Surveillance Area seasons are not modelled here.
// The 2025 array is retained as the prior season's record.

const OH: StateRegulationData = {
  dnrUrl: 'https://ohiodnr.gov/discover-and-learn/safety-conservation/about-ODNR/news',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-09-26',
        closerDate: '2027-02-07',
        name: 'Archery Deer',
        notes: 'Statewide archery season.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-30',
        closerDate: '2026-12-06',
        name: 'Firearms Deer (First)',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-12-19',
        closerDate: '2026-12-20',
        name: 'Firearms Deer (Second)',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2027-01-02',
        closerDate: '2027-01-05',
        name: 'Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2026-11-21',
        closerDate: '2026-11-22',
        name: 'Youth Deer Gun Season',
      },
    ],
    2025: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2025-09-27',
        closerDate: '2026-02-01',
        name: 'Archery Deer',
        notes: 'Statewide archery season.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2025-12-01',
        closerDate: '2025-12-07',
        name: 'Firearms Deer (First)',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2025-12-20',
        closerDate: '2025-12-21',
        name: 'Firearms Deer (Second)',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-01-03',
        closerDate: '2026-01-06',
        name: 'Muzzleloader Deer',
      },
      {
        seasonType: SeasonType.Youth,
        openerDate: '2025-11-22',
        closerDate: '2025-11-23',
        name: 'Youth Deer Gun Season',
      },
    ],
  },
};

// ─── Pennsylvania ────────────────────────────────────────────────────────────
// SOURCE: https://www.pa.gov/agencies/pgc/newsroom/preliminary-2026-27-hunting-seasons-and-bag-limits-approved
// (verified 2026-10-19) — 2026-27 seasons approved by PA Game Commission

const PA: StateRegulationData = {
  dnrUrl: 'https://www.pgc.pa.gov/HuntTrap/Pages/DeerHunting.aspx',
  lastUpdated: '2026-10-19',
  seasons: {
    2026: [
      {
        seasonType: SeasonType.Bow,
        openerDate: '2026-10-03',
        closerDate: '2026-11-20',
        name: 'Archery Deer',
        notes:
          'Statewide. WMUs 2B, 5C and 5D run Sep 19 – Nov 27. Archery resumes Dec 26 – Jan 24, 2027.',
      },
      {
        seasonType: SeasonType.Gun,
        openerDate: '2026-11-28',
        closerDate: '2026-12-13',
        name: 'Regular Firearms Deer',
        notes: 'Statewide antlered and antlerless. Opens Saturday after Thanksgiving.',
      },
      {
        seasonType: SeasonType.Muzzleloader,
        openerDate: '2026-10-17',
        closerDate: '2026-10-25',
        name: 'Muzzleloader Deer (Antlerless)',
        notes: 'Antlerless deer only.',
      },
    ],
  },
};

// ─── Registry ─────────────────────────────────────────────────────────────────

const SEASON_REGULATIONS: Record<string, StateRegulationData> = {
  WI,
  MN,
  MI,
  IA,
  IL,
  OH,
  PA,
};

// ─── Exports ──────────────────────────────────────────────────────────────────

/**
 * Returns season regulations for the given state, season type, and year.
 *
 * - Returns `null` if the state is not in the database.
 * - Returns `null` if the year has no data for that state (e.g., not yet published).
 * - Returns `null` if no seasons match the given season type.
 *
 * Null return signals the caller to fall back to manual date entry.
 */
export function getSeasonDates(
  state: string,
  seasonType: SeasonType,
  year: number,
): SeasonRegulation[] | null {
  const stateData = SEASON_REGULATIONS[state.toUpperCase()];
  if (!stateData) return null;

  const yearSeasons = stateData.seasons[year];
  if (!yearSeasons) return null;

  const matching = yearSeasons.filter((s) => s.seasonType === seasonType);
  return matching.length > 0 ? matching : null;
}

/**
 * Returns regulation meta (DNR URL, last updated) for the given state.
 * Returns `null` if the state is not in the database.
 * Used to render stale-data warnings and DNR verification links.
 */
export function getStateRegulationMeta(state: string): StateRegulationMeta | null {
  const stateData = SEASON_REGULATIONS[state.toUpperCase()];
  if (!stateData) return null;
  return { dnrUrl: stateData.dnrUrl, lastUpdated: stateData.lastUpdated };
}
